import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Mic } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { getUserLocalData } from "../lib/helper";
import PatientListDialog from "../popup/PatientListDialog";
import DashboardLayout from "./DashboardLayout";

const PatientSearch = () => {
  const navigate = useNavigate();
  const { token } = getUserLocalData();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");
  const { transcript, listening, resetTranscript } = useSpeechRecognition();

  // Update query when voice transcript changes
  useEffect(() => {
    if (transcript) {
      setQuery(transcript);
    }
  }, [transcript]);

  // When voice stops, automatically search
  useEffect(() => {
    if (!listening && transcript.trim()) {
      handleSearch();
    }
  }, [listening]);

  const handleSearch = async (e) => {
    e?.preventDefault();

    const searchQuery = query.trim() || transcript.trim();
    if (!searchQuery) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_QUERY_URL}?query=${encodeURIComponent(searchQuery)}&access_token=${token}&identity=CLARA`
      );

      if (response?.data) {
        setPatientData(response?.data?.patient);
        setResponseMessage(response?.data?.response);
        setOpen(true);
      }
    } catch (error) {
      console.error("Error searching patient:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceClick = () => {
    if (!SpeechRecognition.browserSupportsSpeechRecognition()) {
      alert("Your browser doesn't support speech recognition");
      return;
    }

    // Start listening on the same page
    SpeechRecognition.startListening({ continuous: false });
  };

  const clearForm = () => {
    setQuery("");
    resetTranscript();
  };

  return (
    <DashboardLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          minHeight: "calc(100vh - 60px)",
          color: "#fff",
          px: { xs: 2, sm: 4, md: 6 },
          pt: open ? 4 : 0,
          transition: "all 0.3s ease",
          width: "100%",
        }}
      >
        {/* Main Content */}
        <Box sx={{ zIndex: 1, maxWidth: 900, width: "90%" }}>
        {/* Title */}
        <Typography variant="h4" fontWeight={700} textAlign="center" mb={1}>
          Patient Search
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "#b0b0b0", mb: 5, textAlign: "center" }}
        >
          Search by patient name, MRN, condition, or care program
        </Typography>

        {/* Search Box with Mic */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
            background: 'rgba(200, 200, 200, 0.25)',
            backdropFilter: 'blur(12px)',
            borderRadius: '50px',
            padding: '4px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
          }}
        >
          <TextField
            fullWidth
            placeholder={listening ? "Listening..." : "Jhon"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch(e);
              }
            }}
            disabled={loading || listening}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: listening ? '#00e6b0' : '#00D173' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                    disabled={loading || listening || !query.trim()}
                    sx={{
                      color: '#00D173',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 209, 115, 0.1)',
                      },
                    }}
                  >
                    <Search />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff !important',
                backgroundColor: 'transparent',
                borderRadius: '50px',
                paddingRight: '8px',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-disabled': {
                  color: '#fff !important',
                },
              },
              '& .MuiInputBase-input': {
                padding: '12px 8px',
                color: '#fff !important',
                WebkitTextFillColor: '#fff !important',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                color: '#fff !important',
                WebkitTextFillColor: '#fff !important',
              },
              '& .MuiInputBase-input::placeholder': {
                color: '#94A3B8',
                opacity: 1,
              },
            }}
          />

          {/* Mic Button */}
          <IconButton
            onClick={handleVoiceClick}
            disabled={loading}
            sx={{
              backgroundColor: listening ? '#00e6b0' : '#00D173',
              color: '#000',
              width: 48,
              height: 48,
              '&:hover': {
                backgroundColor: '#00e6b0',
              },
              '&:disabled': {
                backgroundColor: '#666',
                color: '#999',
              },
              animation: listening ? 'pulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.05)',
                },
              },
            }}
          >
            <Mic />
          </IconButton>
        </Box>
      </Box>

        {/* Patient Selection Dialog */}
        {open && (
          <PatientListDialog
            open={open}
            setOpen={setOpen}
            data={patientData}
            message={responseMessage}
            clearMessages={clearForm}
          />
        )}
      </Box>
    </DashboardLayout>
  );
};

export default PatientSearch;
